import { BooleanValue } from "../../../../components/ds2/BooleanValue";
import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import {
  AddIcon,
  AsyncSelect,
  Button,
  FlexContent,
  Form,
  FormField,
  InlineOrderEditor,
  ModalDialog,
  Numeric,
  Select,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import Visibility from "../../../../components/ui/Visibility";
import VisibilityScope from "../../../../components/ui/VisibilityScope";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminLock } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { SearchAdminItem } from "../../../../http/types/items";
import { useItemsApi } from "../../../../http/useItems";
import { PaginationQuery } from "../../../../types";

type ItemOption = Pick<SearchAdminItem, "id" | "code" | "description">;

interface FormBody {
  item: ItemOption | null;
  lockModuleId: { value: string; label: string } | null;
  family: "MIDI" | "MINI" | null;
  width: number | null;
  screwEdgeThickness: number | null;
  nModules: number | null;
  nInclinedUplift: number | null;
  nInclinedLateral: number | null;
  evoItem: ItemOption | null;
  visibility: "public" | "internal" | "hidden" | null;
}

const AdminLocksTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "connectors" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useLocks(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const updateVisibility = async (item: AdminLock, _visibility: "public" | "internal" | "hidden") => {
    try {
      await adminApi.updateLock(item.item.id, { visibility: _visibility });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [itemToDelete, setItemToDelete] = useState<AdminLock>();
  const [itemToEdit, setItemToEdit] = useState<AdminLock>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await adminApi.updateLockOrder(id, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminLock>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.family}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.width}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.height}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.thickness}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.screwEdgeThickness}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.nModules}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.lockCalculatorModule.admin.connectors.nInclinedUplift}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.lockCalculatorModule.admin.connectors.nInclinedLateral}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.lockModule}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectors.evoAvailable}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.item.code}</TableCell>
              <TableCell>{item.family}</TableCell>
              <TableCell>{item.width}</TableCell>
              <TableCell>{item.lockModule?.assemblyHeight ?? "—"}</TableCell>
              <TableCell>{item.thickness}</TableCell>
              <TableCell>{item.screwEdgeThickness ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.nModules}</TableCell>
              <TableCell align="center">{item.nInclinedUplift ?? "—"}</TableCell>
              <TableCell align="center">{item.nInclinedLateral ?? "—"}</TableCell>
              <TableCell>{item.lockModule?.code ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>
                {item.visibility != null && (
                  <Visibility
                    value={item.visibility as "public" | "internal" | "hidden"}
                    onChange={(nextVisibility) => updateVisibility(item, nextVisibility)}
                  />
                )}
              </TableCell>
              <TableCell><BooleanValue value={item.evoAvailable} /></TableCell>
              <TableCell align="center">
                <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.item.id, newOrder)} />
              </TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.connectors.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteLock(itemToDelete.item.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const FormModal: FC<{ item?: AdminLock; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const itemsApi = useItemsApi();
  const [defaultOptions, setDefaultOptions] = useState<ItemOption[]>([]);
  const [defaultLockModuleOptions, setDefaultLockModuleOptions] = useState<{ value: string; label: string }[]>([]);
  const [defaultLockModuleValue, setDefaultLockModuleValue] = useState<{ value: string; label: string } | null>(
    null,
  );
  const [defaultEvoItem, setDefaultEvoItem] = useState<ItemOption | null>(null);
  const [formKey, setFormKey] = useState(0);
  const aborter = useRef(new AbortController());

  useEffect(() => {
    itemsApi
      .getAdminItems({ limit: 5, q: "" }, aborter.current.signal)
      .then((items) => setDefaultOptions(items.map((x) => ({ id: x.id, code: x.code, description: x.description }))))
      .catch(() => setDefaultOptions([]));
  }, []);

  useEffect(() => {
    adminApi
      .getLockModules({ q: "", page: 1, limit: 100 }, new AbortController().signal)
      .then((items) => {
        const options = items.map((x) => ({ value: x.id, label: x.code }));
        setDefaultLockModuleOptions(options);
        if (item) {
          const found = options.find((o) => o.value === item.lockModule?.id);
          if (found) {
            setDefaultLockModuleValue(found);
            setFormKey((prev) => prev + 1); // force form remount with correct default
          }
        }
      })
      .catch(() => setDefaultLockModuleOptions([]));
  }, []);

  useEffect(() => {
    if (!item?.evoItem) return;
    const ctrl = new AbortController();
    fetch(`/admin/items/${item.evoItem.id}`, { signal: ctrl.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data) {
          setDefaultEvoItem({ id: data.id, code: data.code, description: data.description ?? "" });
          setFormKey((prev) => prev + 1);
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [item?.evoItem]);

  const loadOptions = async (inputValue: string): Promise<ItemOption[]> => {
    aborter.current.abort();
    aborter.current = new AbortController();
    const items = await itemsApi.getAdminItems({ limit: 5, q: inputValue }, aborter.current.signal);
    return items.map((x) => ({ id: x.id, code: x.code, description: x.description }));
  };

  const defaultValues: FormBody = {
    item: item ? { id: item.item.id, code: item.item.code, description: item.item.description } : null,
    lockModuleId: defaultLockModuleValue,
    family: item?.family ?? null,
    width: item?.width ?? null,
    screwEdgeThickness: item?.screwEdgeThickness ?? null,
    nModules: item?.nModules ?? null,
    nInclinedUplift: item?.nInclinedUplift ?? null,
    nInclinedLateral: item?.nInclinedLateral ?? null,
    evoItem: defaultEvoItem,
    visibility: (item?.visibility as "public" | "internal" | "hidden") ?? "hidden",
  };

  const onSubmit = () => async (formData: FormBody) => {
    if (!formData.item || formData.width == null) return;
    try {
      if (item) {
        await adminApi.updateLock(item.item.id, {
          family: formData.family,
          width: formData.width,
          screwEdgeThickness: formData.screwEdgeThickness,
          nModules: formData.nModules || null,
          nInclinedUplift: formData.nInclinedUplift ?? null,
          nInclinedLateral: formData.nInclinedLateral ?? null,
          evoItemId: formData.evoItem?.id ?? null,
          visibility: formData.visibility ?? null,
        });
      } else {
        if (!formData.family) return;
        await adminApi.insertLock({
          itemId: formData.item.id,
          lockModuleId: formData.lockModuleId?.value ?? "",
          family: formData.family,
          width: formData.width,
          screwEdgeThickness: formData.screwEdgeThickness,
          nModules: formData.nModules || 1,
          nInclinedUplift: formData.nInclinedUplift ?? 0,
          nInclinedLateral: formData.nInclinedLateral ?? 0,
          evoItemId: formData.evoItem?.id ?? null,
          visibility: formData.visibility ?? "hidden",
        });
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        item
          ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.connectors.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.connectors.singular}`
      }
    >
      <Form<FormBody>
        key={formKey}
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.code} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      inputId={labelId}
                      defaultOptions={defaultOptions}
                      loadOptions={loadOptions}
                      isMulti={false}
                      isDisabled={!!item}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => (x.description ? `${x.code} - ${x.description}` : x.code)}
                      getOptionValue={(x) => x.id}
                      value={defaultOptions.find((opt) => opt.id === field.value?.id) ?? field.value}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="lockModuleId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.lockModule}
                  error={fieldState.error}
                 
                >
                  {({ labelId }) => (
                    <AsyncSelect<{ value: string; label: string }, false>
                      inputId={labelId}
                      cacheOptions
                      defaultOptions
                      loadOptions={(inputValue) =>
                        adminApi
                          .getLockModules({ q: inputValue || "", page: 1, limit: 20 }, new AbortController().signal)
                          .then((items) =>
                            items.map((item) => ({
                              value: item.id,
                              label: item.code,
                            })),
                          )
                      }
                      isMulti={false}
                      isDisabled={!!item}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                      value={defaultLockModuleOptions.find((o) => o.value === field.value?.value) ?? field.value}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="family"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.family}
                  error={fieldState.error}
                 
                >
                  {({ labelId }) => (
                    <Select<{ value: "MIDI" | "MINI"; label: string }, false>
                      inputId={labelId}
                      options={[
                        { value: "MIDI", label: "MIDI" },
                        { value: "MINI", label: "MINI" },
                      ]}
                      isMulti={false}
                      isInvalid={fieldState.invalid}
                      getOptionLabel={(x) => x.label}
                      getOptionValue={(x) => x.value}
                      value={field.value ? { value: field.value, label: field.value } : null}
                      onChange={(option) => field.onChange(option?.value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="width"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.width}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="screwEdgeThickness"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.screwEdgeThickness}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="nModules"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.nModules}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="nInclinedUplift"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.nInclinedUplift}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="nInclinedLateral"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.nInclinedLateral}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="evoItem"
              control={control}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectors.evoAvailable}
                  error={fieldState.error}
                 
                >
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      inputId={labelId}
                      defaultOptions={defaultOptions}
                      loadOptions={loadOptions}
                      isMulti={false}
                      isClearable
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => (x.description ? `${x.code} - ${x.description}` : x.code)}
                      getOptionValue={(x) => x.id}
                      value={defaultOptions.find((opt) => opt.id === field.value?.id) ?? field.value}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {() => <VisibilityScope value={field.value} onChange={(value) => field.onChange(value)} />}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminLocksTab;
